// web/src/store/hedgeAccounting/hedgeAccountingSlice.ts
import { MultipleAutocompleteResponse } from '@/components/AutoComplete';
// Import all thunks from the thunks.ts file
import {
  calculateAmortEffectiveRate,
  createAndResetJournalEntries,
  createHypoTrade,
  createJournalEntries,
  createJournalEntryMovements,
  createTransactionAmort,
  createTransactionAmortSetSchedule,
  createTransactionEffectiveness,
  createTransactionNoHedgeAcc,
  deleteTradeDesignationDate,
  deleteTransactionAmort,
  deleteTransactionAmortSetSchedule,
  deleteTransactionEffectiveness,
  editJournalEntryMovementComment,
  fetchBulkTransactionTitles,
  fetchTransactionDetail,
  fetchTransactionTitleStructured,
  fetchBulkTransactionTitlesStructured,
  fetchEffectivenessTypes,
  fetchHedgeAmortData,
  fetchJournalEntries,
  fetchJournalEntryMovementStandards,
  fetchJournalEntryMovements,
  fetchJournalEntryTypes,
  fetchTransactionAmortSetSchedule,
  fetchTransactionAmortTypes,
  fetchTransactionEffectivenessList,
  fetchTransactionLegs,
  fetchTransactionRelations,
  fetchTransactionTitle,
  fetchTransactionsWithoutHedgeAcc,
  getCompanyEntityHedgingStandard,
  getIneffectivenessValues,
  getTradeDesignationDate,
  initialAmortEffectiveRate,
  patchTransactionAmort,
  patchTransactionEffectiveness,
  postIneffectivenessValues,
  postTradeDesignationDate,
  searchTransactionAmortSetSchedule,
  updateTransactionAmort,
  updateTransactionEffectiveness,
  updateTransactionFields,
  updateTransactionNoHedgeAcc,
} from '@pages/HedgeAccountingAdmin/store/thunks';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  HedgeAccountingState,
  HedgeAmortData,
  JournalEntryMovement,
  TransactionAmortSetScheduleData,
  UpdateScheduleFieldPayload,
} from './types';

const initialState: HedgeAccountingState = {
  effectivenessTypes: [],
  effectivenessTypesLoading: false,
  effectivenessTypesError: null,
  journalEntryTypes: [],
  journalEntryTypesLoading: false,
  journalEntryTypesError: null,
  transactionAmortTypes: [],
  transactionAmortTypesLoading: false,
  transactionAmortTypesError: null,
  selectedTrades: [] as MultipleAutocompleteResponse,
  selectedEntity: [] as MultipleAutocompleteResponse,
  hedgeAmortData: [],
  hedgeAmortLoading: false,
  hedgeAmortError: null,
  transactionEffectivenessData: [],
  transactionEffectivenessLoading: false,
  transactionEffectivenessError: null,
  transactionRelations: [],
  transactionRelationsLoading: false,
  transactionRelationsError: null,
  transactionsWithoutHedgeAcc: [],
  transactionsWithoutHedgeAccLoading: false,
  transactionsWithoutHedgeAccError: null,
  transactionTitle: {},
  transactionTitleLoading: false,
  transactionTitleError: null,
  tradeDesignationDate: {},
  tradeDesignationDateLoading: false,
  tradeDesignationDateError: null,
  ineffectivenessValues: {},
  ineffectivenessValuesLoading: false,
  ineffectivenessValuesError: null,
  journalEntries: [],
  journalEntriesLoading: false,
  journalEntriesError: null,
  journalEntryMovementStandards: [],
  journalEntryMovementStandardsLoading: false,
  journalEntryMovementStandardsError: null,
  journalEntryMovements: [],
  journalEntryMovementsLoading: false,
  journalEntryMovementsError: null,
  initAmortEffectiveRateResponse: null,
  initAmortEffectiveRateLoading: false,
  initAmortEffectiveRateError: null,
  companyEntityHedgingStandard: {},
  companyEntityHedgingStandardLoading: false,
  companyEntityHedgingStandardError: null,
  journalEntryCreationStatus: 'idle',
  journalEntryCreationError: null,
  transactionAmortLoading: false,
  transactionAmortError: null,
  transactionAmortSchedules: {},
  transactionAmortSchedulesLoading: false,
  transactionAmortSchedulesError: null,
  transactionAmortCalculateData: null,
  titleFilters: {
    notional: [],
    instrumentType: [],
    maturityDate: [],
    searchText: '',
  },
  transactionLegs: {},
  transactionLegsLoading: false,
  transactionLegsError: null,
  bulkTransactionTitles: [],
  bulkTransactionTitlesLoading: false,
  bulkTransactionTitlesError: null,
  bulkTransactionTitlesPagination: null,
  bulkApiFilters: {
    page_size: 20,
  },
};

const hedgeAccountingSlice = createSlice({
  name: 'hedgeAccounting',
  initialState,
  reducers: {
    setSelectedTrades: (
      state,
      action: PayloadAction<MultipleAutocompleteResponse>,
    ) => {
      state.selectedTrades = action.payload;
    },
    setSelectedEntity: (
      state,
      action: PayloadAction<MultipleAutocompleteResponse>,
    ) => {
      state.selectedEntity = action.payload;
    },
    clearSelections: (state) => {
      state.selectedTrades = [];
      state.selectedEntity = [];
    },
    upsertLocalAmortRow(
      state,
      action: PayloadAction<{ data: HedgeAmortData; isNew: boolean }>,
    ) {
      const { data, isNew } = action.payload;

      const index = state.hedgeAmortData.findIndex(
        (r) => r.hedge_acc_amort_id === data.hedge_acc_amort_id,
      );

      if (isNew) {
        if (index === -1) {
          state.hedgeAmortData.push(data);
        }
      } else {
        if (index !== -1) {
          state.hedgeAmortData[index] = data;
        }
      }
    },
    removeLocalAmortRow(state, action: PayloadAction<number>) {
      state.hedgeAmortData = state.hedgeAmortData.filter(
        (r) => r.hedge_acc_amort_id !== action.payload,
      );
    },
    setTransactionAmortSchedules(
      state,
      action: PayloadAction<Record<number, TransactionAmortSetScheduleData[]>>,
    ) {
      state.transactionAmortSchedules = {
        ...state.transactionAmortSchedules,
        ...action.payload,
      };
    },
    resetTransactionAmortCalculateData(state) {
      state.transactionAmortCalculateData = null;
    },
    updateScheduleField: (
      state,
      action: PayloadAction<UpdateScheduleFieldPayload>,
    ) => {
      const { amortId, scheduleId, field, value } = action.payload;

      const scheduleList = state.transactionAmortSchedules[amortId];

      if (!scheduleList) {
        return;
      }

      const target = scheduleList.find(
        (item) =>
          item.hedge_acc_trans_amort_set_schedule_id === Number(scheduleId),
      );

      if (target) {
        (target as any)[field] = value;
      }
    },
    setTitleFilters: (
      state,
      action: PayloadAction<{
        notional?: string[];
        instrumentType?: string[];
        maturityDate?: string[];
        searchText?: string;
      }>,
    ) => {
      state.titleFilters = {
        ...state.titleFilters,
        ...action.payload,
      };
    },
    clearTitleFilters: (state) => {
      state.titleFilters = {
        notional: [],
        instrumentType: [],
        maturityDate: [],
        searchText: '',
      };
    },
    clearTransactionTitles: (state) => {
      state.transactionTitle = {};
      state.bulkTransactionTitles = [];
      state.bulkTransactionTitlesPagination = null;
    },
    setBulkTitlesPage: (state, action: PayloadAction<number>) => {
      if (state.bulkTransactionTitlesPagination) {
        state.bulkTransactionTitlesPagination.page = action.payload;
      }
    },
    setBulkTitlesPageSize: (state, action: PayloadAction<number>) => {
      if (state.bulkTransactionTitlesPagination) {
        state.bulkTransactionTitlesPagination.page_size = action.payload;
      }
    },
    setBulkApiFilters: (
      state,
      action: PayloadAction<Partial<HedgeAccountingState['bulkApiFilters']>>,
    ) => {
      state.bulkApiFilters = {
        ...state.bulkApiFilters,
        ...action.payload,
      };
    },
    clearBulkApiFilters: (state) => {
      state.bulkApiFilters = {
        page_size: 20,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Journal Entry Types
      .addCase(fetchJournalEntryTypes.pending, (state) => {
        state.journalEntryTypesLoading = true;
        state.journalEntryTypesError = null;
      })
      .addCase(fetchJournalEntryTypes.fulfilled, (state, action) => {
        state.journalEntryTypesLoading = false;
        state.journalEntryTypes = action.payload;
      })
      .addCase(fetchJournalEntryTypes.rejected, (state, action) => {
        state.journalEntryTypesLoading = false;
        state.journalEntryTypesError = action.payload as string;
      })

      // Journal Entries
      .addCase(fetchJournalEntries.pending, (state) => {
        state.journalEntriesLoading = true;
        state.journalEntriesError = null;
      })
      .addCase(fetchJournalEntries.fulfilled, (state, action) => {
        state.journalEntriesLoading = false;
        state.journalEntries = action.payload;
      })
      .addCase(fetchJournalEntries.rejected, (state, action) => {
        state.journalEntriesLoading = false;
        state.journalEntriesError = action.payload as string;
      })

      // Effectiveness Types
      .addCase(fetchEffectivenessTypes.pending, (state) => {
        state.effectivenessTypesLoading = true;
        state.effectivenessTypesError = null;
      })
      .addCase(fetchEffectivenessTypes.fulfilled, (state, action) => {
        state.effectivenessTypesLoading = false;
        state.effectivenessTypes = action.payload;
      })
      .addCase(fetchEffectivenessTypes.rejected, (state, action) => {
        state.effectivenessTypesLoading = false;
        state.effectivenessTypesError = action.payload as string;
      })

      // Transaction Amortization Types
      .addCase(fetchTransactionAmortTypes.pending, (state) => {
        state.transactionAmortTypesLoading = true;
        state.transactionAmortTypesError = null;
      })
      .addCase(fetchTransactionAmortTypes.fulfilled, (state, action) => {
        state.transactionAmortTypesLoading = false;
        state.transactionAmortTypes = action.payload;
      })
      .addCase(fetchTransactionAmortTypes.rejected, (state, action) => {
        state.transactionAmortTypesLoading = false;
        state.transactionAmortTypesError = action.payload as string;
      })

      // Journal Entry Movement Standards
      .addCase(fetchJournalEntryMovementStandards.pending, (state) => {
        state.journalEntryMovementStandardsLoading = true;
        state.journalEntryMovementStandardsError = null;
      })
      .addCase(
        fetchJournalEntryMovementStandards.fulfilled,
        (state, action) => {
          state.journalEntryMovementStandardsLoading = false;
          state.journalEntryMovementStandards = action.payload;
        },
      )
      .addCase(fetchJournalEntryMovementStandards.rejected, (state, action) => {
        state.journalEntryMovementStandardsLoading = false;
        state.journalEntryMovementStandardsError = action.payload as string;
      })

      // Hedge Amortization Data
      .addCase(fetchHedgeAmortData.pending, (state) => {
        state.hedgeAmortLoading = true;
        state.hedgeAmortError = null;
      })
      .addCase(fetchHedgeAmortData.fulfilled, (state, action) => {
        state.hedgeAmortLoading = false;
        state.hedgeAmortData = action.payload;
      })
      .addCase(fetchHedgeAmortData.rejected, (state, action) => {
        state.hedgeAmortLoading = false;
        state.hedgeAmortError = action.payload as string;
      })

      // Initial Amort Effective Rate
      .addCase(initialAmortEffectiveRate.pending, (state) => {
        state.initAmortEffectiveRateLoading = true;
        state.initAmortEffectiveRateError = null;
      })
      .addCase(initialAmortEffectiveRate.fulfilled, (state, action) => {
        state.initAmortEffectiveRateLoading = false;
        state.initAmortEffectiveRateResponse = action.payload;
      })
      .addCase(initialAmortEffectiveRate.rejected, (state, action) => {
        state.initAmortEffectiveRateLoading = false;
        state.initAmortEffectiveRateError = action.payload as string;
      })

      // Create Transaction Amort
      .addCase(createTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(createTransactionAmort.fulfilled, (state) => {
        state.transactionAmortLoading = false;
      })
      .addCase(createTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      // Update Transaction Amort
      .addCase(updateTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(updateTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        // Update existing amort data in the state
        const index = state.hedgeAmortData.findIndex(
          (item) =>
            item.hedge_acc_amort_id === action.payload.hedge_acc_amort_id,
        );
        if (index !== -1) {
          state.hedgeAmortData[index] = action.payload;
        }
      })
      .addCase(updateTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      // Patch Transaction Amort
      .addCase(patchTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(patchTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        // Update existing amort data in the state
        const index = state.hedgeAmortData.findIndex(
          (item) =>
            item.hedge_acc_amort_id === action.payload.hedge_acc_amort_id,
        );
        if (index !== -1) {
          state.hedgeAmortData[index] = action.payload;
        }
      })
      .addCase(patchTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      // Delete Transaction Amort
      .addCase(deleteTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(deleteTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        // Remove deleted amort from state
        state.hedgeAmortData = state.hedgeAmortData.filter(
          (item) => item.hedge_acc_amort_id !== action.payload,
        );
      })
      .addCase(deleteTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      // Calculate Amort Effective Rate
      .addCase(calculateAmortEffectiveRate.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(calculateAmortEffectiveRate.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortCalculateData = action.payload;
      })
      .addCase(calculateAmortEffectiveRate.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      // Fetch Transaction Amort Set Schedule
      .addCase(fetchTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(fetchTransactionAmortSetSchedule.fulfilled, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        if (action.meta.arg) {
          // Store schedule for the specific amort ID
          state.transactionAmortSchedules[action.meta.arg] = action.payload;
        }
      })
      .addCase(fetchTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      // Create Transaction Amort Set Schedule
      .addCase(createTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(createTransactionAmortSetSchedule.fulfilled, (state) => {
        state.transactionAmortSchedulesLoading = false;
      })
      .addCase(createTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      // Search Transaction Amort Set Schedule
      .addCase(searchTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(searchTransactionAmortSetSchedule.fulfilled, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        // Update schedules for multiple amortizations
        state.transactionAmortSchedules = {
          ...state.transactionAmortSchedules,
          ...action.payload,
        };
      })
      .addCase(searchTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      // Transaction Effectiveness Data
      .addCase(fetchTransactionEffectivenessList.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(fetchTransactionEffectivenessList.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessData = action.payload;
      })
      .addCase(fetchTransactionEffectivenessList.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      // Create Transaction Effectiveness
      .addCase(createTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(createTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        // Add the new transaction effectiveness data to the state
        if (action.payload) {
          state.transactionEffectivenessData.push(action.payload);
        }
      })
      .addCase(createTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      // Update Transaction Effectiveness
      .addCase(updateTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(updateTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        // Update the transaction effectiveness data in the state
        const index = state.transactionEffectivenessData.findIndex(
          (item) =>
            item.hedge_acc_trans_eff_id ===
            action.payload.hedge_acc_trans_eff_id,
        );
        if (index !== -1) {
          state.transactionEffectivenessData[index] = action.payload;
        }
      })
      .addCase(updateTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      // Patch Transaction Effectiveness
      .addCase(patchTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(patchTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        // Update the transaction effectiveness data in the state
        const index = state.transactionEffectivenessData.findIndex(
          (item) =>
            item.hedge_acc_trans_eff_id ===
            action.payload.hedge_acc_trans_eff_id,
        );
        if (index !== -1) {
          state.transactionEffectivenessData[index] = action.payload;
        }
      })
      .addCase(patchTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      // Delete Transaction Effectiveness
      .addCase(deleteTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(deleteTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        // Remove the deleted transaction effectiveness data from the state
        state.transactionEffectivenessData =
          state.transactionEffectivenessData.filter(
            (item) => item.hedge_acc_trans_eff_id !== action.payload,
          );
      })
      .addCase(deleteTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError =
          action.payload || 'Failed to delete transaction effectiveness';
      })

      // Transaction Relations
      .addCase(fetchTransactionRelations.pending, (state) => {
        state.transactionRelationsLoading = true;
        state.transactionRelationsError = null;
      })
      .addCase(fetchTransactionRelations.fulfilled, (state, action) => {
        state.transactionRelationsLoading = false;
        state.transactionRelations = action.payload;
      })
      .addCase(fetchTransactionRelations.rejected, (state, action) => {
        state.transactionRelationsLoading = false;
        state.transactionRelationsError = action.payload as string;
      })

      // Transactions Without Hedge Accounting
      .addCase(fetchTransactionsWithoutHedgeAcc.pending, (state) => {
        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(fetchTransactionsWithoutHedgeAcc.fulfilled, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAcc = action.payload;
      })
      .addCase(fetchTransactionsWithoutHedgeAcc.rejected, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      // Transaction Titles
      .addCase(fetchTransactionTitle.pending, (state) => {
        state.transactionTitleLoading = true;
        state.transactionTitleError = null;
      })
      .addCase(fetchTransactionTitle.fulfilled, (state, action) => {
        state.transactionTitleLoading = false;
        state.transactionTitle[action.meta.arg.transactionId] = {
          title: action.payload,
        };
      })
      .addCase(fetchTransactionTitle.rejected, (state, action) => {
        state.transactionTitleLoading = false;
        state.transactionTitleError =
          action.payload || 'Failed to fetch transaction title';
      })

      // Fetch Bulk Transaction Titles
      .addCase(fetchBulkTransactionTitles.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchBulkTransactionTitles.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitles = action.payload.transaction_titles;
        state.bulkTransactionTitlesPagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total_transactions: action.payload.total_transactions,
          total_pages: action.payload.total_pages,
          transactions_in_page: action.payload.transactions_in_page,
          processing_time_seconds: action.payload.processing_time_seconds,
        };
      })
      .addCase(fetchBulkTransactionTitles.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch bulk transaction titles';
      })

      // Fetch Transaction Detail (individual)
      .addCase(fetchTransactionDetail.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchTransactionDetail.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        // Add or update the transaction in the array
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(fetchTransactionDetail.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch transaction detail';
      })

      // Fetch Transaction Title Structured (individual)
      .addCase(fetchTransactionTitleStructured.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchTransactionTitleStructured.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        // Add or update the transaction in the array
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(fetchTransactionTitleStructured.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch transaction title structured';
      })

      // Fetch Bulk Transaction Titles Structured
      .addCase(fetchBulkTransactionTitlesStructured.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchBulkTransactionTitlesStructured.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        // Replace all bulk transaction titles with the new batch
        state.bulkTransactionTitles = action.payload;
      })
      .addCase(fetchBulkTransactionTitlesStructured.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch bulk transaction titles structured';
      })

      // Fetch Transaction Legs
      .addCase(fetchTransactionLegs.pending, (state) => {
        state.transactionLegsLoading = true;
        state.transactionLegsError = null;
      })
      .addCase(fetchTransactionLegs.fulfilled, (state, action) => {
        state.transactionLegsLoading = false;
        state.transactionLegs[action.payload.transactionId] = action.payload.legs;
      })
      .addCase(fetchTransactionLegs.rejected, (state, action) => {
        state.transactionLegsLoading = false;
        state.transactionLegsError =
          action.payload || 'Failed to fetch transaction legs';
      })

      // Get Trade Designation Date
      .addCase(getTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(getTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        if (action.payload && action.payload.transaction_id) {
          state.tradeDesignationDate[action.payload.transaction_id] = {
            transaction_id: action.payload.transaction_id,
            designation_date: action.payload.designation_date,
          };
        }
      })
      .addCase(getTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to fetch trade designation date';
      })

      // Post Trade Designation Date
      .addCase(postTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(postTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        const transactionId = action.meta.arg.transaction_id;
        const designationDate = action.meta.arg.designation_date;
        state.tradeDesignationDate[transactionId] = {
          transaction_id: transactionId,
          designation_date: designationDate,
        };
      })
      .addCase(postTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to set trade designation date';
      })

      // Delete Trade Designation Date
      .addCase(deleteTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(deleteTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        if (action.meta.arg) {
          delete state.tradeDesignationDate[action.meta.arg];
        }
      })
      .addCase(deleteTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to delete trade designation date';
      })

      // Ineffectiveness Values
      .addCase(getIneffectivenessValues.pending, (state) => {
        state.ineffectivenessValuesLoading = true;
        state.ineffectivenessValuesError = null;
      })
      .addCase(getIneffectivenessValues.fulfilled, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        if (action.payload && action.meta.arg) {
          state.ineffectivenessValues[action.meta.arg] = action.payload;
        }
      })
      .addCase(getIneffectivenessValues.rejected, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        state.ineffectivenessValuesError =
          action.error.message || 'Failed to fetch ineffectiveness values';
      })
      .addCase(postIneffectivenessValues.pending, (state) => {
        state.ineffectivenessValuesLoading = true;
        state.ineffectivenessValuesError = null;
      })
      .addCase(postIneffectivenessValues.fulfilled, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        if (action.payload && action.meta.arg.transactionId) {
          state.ineffectivenessValues[action.meta.arg.transactionId] =
            action.payload;
        }
      })
      .addCase(postIneffectivenessValues.rejected, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        state.ineffectivenessValuesError =
          action.error.message || 'Failed to post ineffectiveness values';
      })

      // Journal Entry Movements
      .addCase(fetchJournalEntryMovements.pending, (state) => {
        state.journalEntryMovementsLoading = true;
        state.journalEntryMovementsError = null;
      })
      .addCase(
        fetchJournalEntryMovements.fulfilled,
        (state, action: PayloadAction<JournalEntryMovement[]>) => {
          state.journalEntryMovementsLoading = false;
          state.journalEntryMovements = action.payload;
        },
      )
      .addCase(fetchJournalEntryMovements.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.payload || action.error.message || null;
      })

      // Handle creating new journal entry movements
      .addCase(createJournalEntryMovements.pending, (state) => {
        state.journalEntryMovementsLoading = true;
        state.journalEntryMovementsError = null;
      })
      .addCase(
        createJournalEntryMovements.fulfilled,
        (state, _action: PayloadAction<string>) => {
          state.journalEntryMovementsLoading = false;
        },
      )
      .addCase(createJournalEntryMovements.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.payload || action.error.message || null;
      })

      // Handle edit comment thunk
      .addCase(editJournalEntryMovementComment.pending, (state) => {
        state.journalEntryMovementsLoading = true;
      })
      .addCase(
        editJournalEntryMovementComment.fulfilled,
        (state, _action: PayloadAction<string>) => {
          state.journalEntryMovementsLoading = false;
        },
      )
      .addCase(editJournalEntryMovementComment.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.error.message || 'Failed to edit movement comment';
      })

      // Company Entity Hedging Standard
      .addCase(getCompanyEntityHedgingStandard.pending, (state) => {
        state.companyEntityHedgingStandardLoading = true;
        state.companyEntityHedgingStandardError = null;
      })
      .addCase(getCompanyEntityHedgingStandard.fulfilled, (state, action) => {
        state.companyEntityHedgingStandardLoading = false;
        if (action.meta.arg) {
          state.companyEntityHedgingStandard[action.meta.arg] = action.payload;
        }
      })
      .addCase(getCompanyEntityHedgingStandard.rejected, (state, action) => {
        state.companyEntityHedgingStandardLoading = false;
        state.companyEntityHedgingStandardError =
          action.payload || 'Failed to fetch company entity hedging standard';
      })

      // Create Journal Entries
      .addCase(createJournalEntries.pending, (state) => {
        state.journalEntriesLoading = true;
        state.journalEntriesError = null;
      })
      .addCase(createJournalEntries.fulfilled, (state, action) => {
        state.journalEntriesLoading = false;
        // Update journalEntries state if we got a response
        if (action.payload && action.payload.length > 0) {
          // Merge new entries with existing ones, or replace if for same trades
          const newTradeIds = action.payload.map((entry) => entry.trade_id);

          // Filter out existing entries for the same trades
          const filteredEntries = state.journalEntries.filter(
            (entry) => !newTradeIds.includes(entry.trade_id),
          );

          // Add the new entries
          state.journalEntries = [...filteredEntries, ...action.payload];
        }
      })
      .addCase(createJournalEntries.rejected, (state, action) => {
        state.journalEntriesLoading = false;
        state.journalEntriesError = action.payload as string;
      })

      // Create Transaction No Hedge Acc
      .addCase(createTransactionNoHedgeAcc.pending, (state) => {
        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(createTransactionNoHedgeAcc.fulfilled, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;

        // Find existing record (if any)
        const existingIndex = state.transactionsWithoutHedgeAcc.findIndex(
          (item) => item.transaction_id === action.payload.transaction_id,
        );

        if (existingIndex >= 0) {
          // Update existing record
          state.transactionsWithoutHedgeAcc[existingIndex] = action.payload;
        } else {
          // Add new record
          state.transactionsWithoutHedgeAcc.push(action.payload);
        }
      })
      .addCase(createTransactionNoHedgeAcc.rejected, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      // Update Transaction No Hedge Acc
      .addCase(updateTransactionNoHedgeAcc.pending, (state) => {
        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(updateTransactionNoHedgeAcc.fulfilled, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;

        // Find existing record
        const existingIndex = state.transactionsWithoutHedgeAcc.findIndex(
          (item) => item.transaction_id === action.payload.transaction_id,
        );

        if (existingIndex >= 0) {
          // Update existing record
          state.transactionsWithoutHedgeAcc[existingIndex] = action.payload;
        } else {
          // Add as new record if not found
          state.transactionsWithoutHedgeAcc.push(action.payload);
        }
      })
      .addCase(updateTransactionNoHedgeAcc.rejected, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      // Create and Reset Journal Entries
      .addCase(createAndResetJournalEntries.pending, (state) => {
        state.journalEntryCreationStatus = 'loading';
        state.journalEntryCreationError = null;
      })
      .addCase(createAndResetJournalEntries.fulfilled, (state, _action) => {
        state.journalEntryCreationStatus = 'succeeded';
        // No need to update journal entries here as they are already
        // updated by the fetchJournalEntries thunk inside createAndResetJournalEntries
      })
      .addCase(createAndResetJournalEntries.rejected, (state, action) => {
        state.journalEntryCreationStatus = 'failed';
        state.journalEntryCreationError =
          action.error.message || 'Failed to create journal entries';
      })

      // Delete Transaction Amort Set Schedule
      .addCase(deleteTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(deleteTransactionAmortSetSchedule.fulfilled, (state) => {
        state.transactionAmortSchedulesLoading = false;
      })
      .addCase(deleteTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      // Update Transaction Fields
      .addCase(updateTransactionFields.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(updateTransactionFields.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        // Update the transaction in the bulkTransactionTitles array
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(updateTransactionFields.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to update transaction fields';
      });
  },
});

export const {
  setSelectedTrades,
  setSelectedEntity,
  clearSelections,
  upsertLocalAmortRow,
  removeLocalAmortRow,
  setTransactionAmortSchedules,
  resetTransactionAmortCalculateData,
  updateScheduleField,
  setTitleFilters,
  clearTitleFilters,
  clearTransactionTitles,
  setBulkTitlesPage,
  setBulkTitlesPageSize,
  setBulkApiFilters,
  clearBulkApiFilters,
} = hedgeAccountingSlice.actions;

// Export all thunks for use in components
export {
  fetchJournalEntries,
  fetchJournalEntryTypes,
  fetchEffectivenessTypes,
  fetchTransactionAmortTypes,
  fetchJournalEntryMovementStandards,
  fetchHedgeAmortData,
  initialAmortEffectiveRate,
  createTransactionAmort,
  updateTransactionAmort,
  patchTransactionAmort,
  deleteTransactionAmort,
  calculateAmortEffectiveRate,
  fetchTransactionAmortSetSchedule,
  createTransactionAmortSetSchedule,
  searchTransactionAmortSetSchedule,
  fetchTransactionEffectivenessList,
  createTransactionEffectiveness,
  updateTransactionEffectiveness,
  patchTransactionEffectiveness,
  deleteTransactionEffectiveness,
  fetchTransactionRelations,
  fetchTransactionsWithoutHedgeAcc,
  createTransactionNoHedgeAcc,
  updateTransactionNoHedgeAcc,
  updateTransactionFields,
  fetchTransactionTitle,
  fetchBulkTransactionTitles,
  fetchTransactionDetail,
  fetchTransactionTitleStructured,
  fetchBulkTransactionTitlesStructured,
  fetchTransactionLegs,
  createHypoTrade,
  getTradeDesignationDate,
  postTradeDesignationDate,
  deleteTradeDesignationDate,
  getIneffectivenessValues,
  postIneffectivenessValues,
  fetchJournalEntryMovements,
  createJournalEntryMovements,
  editJournalEntryMovementComment,
  getCompanyEntityHedgingStandard,
  createJournalEntries,
  createAndResetJournalEntries,
};

export default hedgeAccountingSlice.reducer;
